"use client";

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, DescriptionList, Field, Loader, TextInput } from '../../../../../ui';
import { Callout, Tabs } from '../ui-pending';
import './chatbotWidget.scss';

// SVG Icons
const ChatIcon = () => (
    <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"></path>
    </svg>
);

const SendIcon = () => (
    <svg className="chatbot-widget-send-icon" aria-hidden="true" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
    </svg>
);

// --- Main ChatbotWidget Component ---
export default function ChatbotWidget({
    isVisible,
    onClose,
    initialEmail = '',
    initialPhoneNumber = '',
    autoStart = false,
    isEmbedded = false, // New prop to differentiate rendering
}) {
    // Chat states
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [isChatLoading, setIsChatLoading] = useState(false);
    const [chatError, setChatError] = useState('');

    // Summary and Customer Info states
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [summary, setSummary] = useState([]); // Array of strings or single string
    const [agentTips, setAgentTips] = useState([]);
    const [actionableInsights, setActionableInsights] = useState([]);
    const [customerInfo, setCustomerInfo] = useState({});
    const [messageSummary, setMessageSummary] = useState(''); // New: Conversation summary text
    const [convoURL, setConvoURL] = useState(''); // New: Conversation URL
    const [ticketURL, setTicketURL] = useState(''); // New: Ticket URL
    const [sentiment, setsentiment] = useState('');
    const [Latestsentiment, setLatestsentiment] = useState('');
    const [emoji, setEmoji] = useState('moderate');
    const [emoji2, setEmoji2] = useState('moderate');

    // UI states
    const [isSummarizing, setIsSummarizing] = useState(false);
    const [summarizationError, setSummarizationError] = useState('');
    const [currentView, setCurrentView] = useState('contactForm'); // 'contactForm', 'loadingSummary', 'chatView', 'error'
    const [activeInfoTab, setActiveInfoTab] = useState('summary'); // 'summary', 'customerInfo', 'agentTips', 'chat'

    // Refs
    const pollingIntervalRef = useRef(null);
    const pollingAttemptsRef = useRef(0);
    const messagesEndRef = useRef(null); // For auto-scrolling chat

    // Backend Endpoints
    const CHATBOT_WEBHOOK_URL = 'https://inbotiqchatbot-bmgveehjh4erazh4.centralindia-01.azurewebsites.net/api/chat';
    const BACKEND_SUBMIT_CONTACT_URL = 'https://inbotiqchatbot-bmgveehjh4erazh4.centralindia-01.azurewebsites.net/api/contact';
    const BACKEND_SUMMARY_URL = 'https://inbotiqchatbot-bmgveehjh4erazh4.centralindia-01.azurewebsites.net/api/data';
    const BACKEND_CLEAR_SUMMARY_URL = 'https://inbotiqchatbot-bmgveehjh4erazh4.centralindia-01.azurewebsites.net/api/clear';

    // --- Effects ---

    // Scroll to the latest message in chat view
    useEffect(() => {
        if (currentView === 'chatView' && activeInfoTab === 'chat') {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages, currentView, activeInfoTab]);

    // Cleanup polling interval on unmount
    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        };
    }, []);

    // Auto start when initial contact info is provided
    useEffect(() => {
        if (autoStart && (initialEmail || initialPhoneNumber)) {
            handleSubmitContact(null, initialEmail, initialPhoneNumber);
        }
    }, [autoStart, initialEmail, initialPhoneNumber]);

    // Auto-start with provided email
    useEffect(() => {
        if (autoStart && initialEmail) {
            setEmail(initialEmail);
            handleSubmitContact(null, initialEmail, '');
        }
    }, [autoStart, initialEmail]);

    // Auto-start with provided phone number
    useEffect(() => {
        if (autoStart && initialPhoneNumber) {
            setPhoneNumber(initialPhoneNumber);
            handleSubmitContact(null, '', initialPhoneNumber);
        }
    }, [autoStart, initialPhoneNumber]);

    const getEmoji = (sentimentType) => {
        switch (sentimentType.toLowerCase()) {
            case 'happy':
                return '😊';
            case 'angry':
                return '😡';
            case 'frustrated':
                return '😩';
            case 'sad':
                return '😞';
            case 'moderate':
                return '😐';
            default:
                return '😐';
        }
    };

    // --- Helper Functions ---
    function parseDateFromSummaryString(summaryLine) {
        const match = summaryLine.match(/([A-Za-z]+)\s+(\d+)\s*–/);
        if (match) {
            const monthStr = match[1];
            const dayStr = match[2];
            const currentYear = new Date().getFullYear(); // Use a dynamic year or a fixed one as needed
            const fullDateString = `${monthStr} ${dayStr}, ${currentYear}`;
            return new Date(fullDateString);
        }
        return new Date('1900-01-01'); // Fallback for unparseable dates
    }

    // Function to clear all widget states and return to contact form
    const resetWidget = useCallback(() => {
        setEmail('');
        setPhoneNumber('');
        setSummary([]);
        setAgentTips([]);
        setActionableInsights([]);
        setCustomerInfo({});
        setMessages([]);
        setChatError('');
        setSummarizationError('');
        setIsSummarizing(false);
        setIsChatLoading(false);
        setMessageSummary('');
        setConvoURL('');
        setTicketURL('');
        setsentiment('');

        setCurrentView('contactForm');
        setActiveInfoTab('summary'); // Reset active tab
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
        pollingAttemptsRef.current = 0;
    }, []);

    // Fetches summary data from the backend
    const fetchSummaryFromBackend = useCallback(async () => {
        setSummarizationError(''); // Clear previous errors
        pollingAttemptsRef.current += 1;
        if (pollingAttemptsRef.current > 10) {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
            setSummarizationError('No data found. Please try again.');
            setCurrentView('error');
            return;
        }

        try {
            const response = await fetch(BACKEND_SUMMARY_URL);

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Backend GET error! Status: ${response.status}. Response: ${errorText}`);
            }

            const data = await response.json();
            console.log("Fetched summary data:", data);

            // Handle 'done' status
            if (data && data.status === 'done') {
                // Parse summary if it's a stringified array
                let parsedSummary = data.summary;
                if (typeof data.summary === 'string') {
                    try {
                        const temp = JSON.parse(data.summary);
                        if (Array.isArray(temp)) {
                            parsedSummary = temp;
                        }
                    } catch (e) {
                        console.warn("Failed to parse summary string as array, using as is.", data.summary);
                    }
                } else if (!Array.isArray(data.summary)) {
                    // Ensure summary is always an array, even if a single string
                    parsedSummary = [data.summary];
                }

                setSummary(parsedSummary);
                setAgentTips(Array.isArray(data.tips) && data.tips.length > 0 ? data.tips.slice(0, 3) : ['No specific tips available.']);
                setActionableInsights(Array.isArray(data.actionable) && data.actionable.length > 0 ? data.actionable : ['No specific actions required, user has no active tickets!']);
                setCustomerInfo(data.customerInfo || {});
                setMessageSummary(data.messagesummary || '');
                setConvoURL(data.convoURL || ''); // Ensure default to empty string
                setTicketURL(data.customerInfo?.url || ''); // Safely access nested URL
                setsentiment(data.SentimentAnalysis || '');
                setLatestsentiment(data.LatestSentimentAnalysis || '');
                setEmoji(data.emoji || '');
                setEmoji2(data.emoji2 || '');

                setCurrentView('chatView');
                setActiveInfoTab('summary'); // Show summary tab by default after loading
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                }
                pollingAttemptsRef.current = 0;
            }
            // Handle 'pending' status
            else if (data && data.status === 'pending') {
                setCurrentView('loadingSummary');
                setSummary(data.summary || ['Summary is still being processed...']);
                setAgentTips(Array.isArray(data.tips) && data.tips.length > 0 ? data.tips.slice(0, 3) : ['Generating tips...']);
                setActionableInsights(Array.isArray(data.actionable) && data.actionable.length > 0 ? data.actionable : ['Generating actionable insights...']);
                setCustomerInfo(data.customerInfo || {});
                setMessageSummary(data.messagesummary || 'Generating conversation summary...');
                setConvoURL(data.convoURL || '');
                setTicketURL(data.customerInfo?.url || '');
                setsentiment(data.SentimentAnalysis || '');
                setLatestsentiment(data.LatestSentimentAnalysis || '');
                setEmoji(data.emoji || '');
                setEmoji2(data.emoji2 || '');

                if (!pollingIntervalRef.current) {
                    pollingIntervalRef.current = setInterval(fetchSummaryFromBackend, 5000);
                }
            }
            // Handle unexpected response
            else {
                setSummarizationError('No summary available yet or unexpected backend response structure.');
                setCurrentView('error');
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                }
            }
        } catch (err) {
            console.error('Error fetching summary from backend:', err);
            setSummarizationError(`Failed to retrieve summary: ${err.message}`);
            setCurrentView('error');
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        }
    }, []); // Empty dependency array as it doesn't depend on external props/state that change

    // --- Event Handlers ---

    // Handles contact form submission
    const handleSubmitContact = async (event, emailOverride, phoneOverride) => {
        if (event && event.preventDefault) {
            event.preventDefault();
        }

        const finalEmail = emailOverride ?? email;
        const finalPhone = phoneOverride ?? phoneNumber;

        resetWidget(); // Clear states before new submission
        pollingAttemptsRef.current = 0;

        setEmail(finalEmail);
        setPhoneNumber(finalPhone);

        if (!finalEmail && !finalPhone) {
            setSummarizationError('Please provide either an email or a phone number.');
            return;
        }

        setIsSummarizing(true);
        setCurrentView('loadingSummary');

        try {
            // Clear backend data first
            await fetch(BACKEND_CLEAR_SUMMARY_URL, { method: 'POST' });
            console.log('Backend data cleared for new submission.');

            // Submit contact info to backend
            const backendResponse = await fetch(BACKEND_SUBMIT_CONTACT_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: finalEmail || null,
                    phoneNumber: finalPhone || null,
                }),
            });

            if (!backendResponse.ok) {
                const errorData = await backendResponse.json().catch(() => ({ message: 'Unknown backend error' }));
                throw new Error(`Backend submission error! Status: ${backendResponse.status}. ${errorData.message || 'Backend responded with an error.'}`);
            }

            const backendResult = await backendResponse.json();
            console.log('Backend response after contact submission:', backendResult);

            // Start polling for summary data
            fetchSummaryFromBackend();

        } catch (err) {
            console.error('Error submitting contact to backend:', err);
            setSummarizationError(`Failed to initiate summary request: ${err.message}`);
            setCurrentView('error');
        } finally {
            setIsSummarizing(false);
        }
    };

    // Handles sending messages to the chatbot
    const handleSendMessage = async (event) => {
        event.preventDefault();
        if (!newMessage.trim() || isChatLoading) return;

        const userMessage = newMessage.trim();

        setMessages(prevMessages => [...prevMessages, { text: userMessage, sender: 'user' }]);
        setNewMessage('');
        setChatError('');
        setIsChatLoading(true);

        try {
            const response = await fetch(CHATBOT_WEBHOOK_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    sessionId: "1093",
                    message: userMessage,
                    contact: { email: email, phoneNumber: phoneNumber },
                    history: messages.map(msg => ({ text: msg.text, sender: msg.sender })),
                    summary: summary,
                    agentTips: agentTips,
                    customerInfo: customerInfo,
                    actionableInsights: actionableInsights,
                    messagesummary: messageSummary,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Unknown chatbot error' }));
                throw new Error(`Chatbot backend error! Status: ${response.status}. ${errorData.message || 'Chatbot responded with an error.'}`);
            }

            const data = await response.json();
            console.log("Chatbot response:", data);
            const botResponse = data.output || "Sorry, I couldn't understand that. Please try again.";

            setMessages(prevMessages => [...prevMessages, { text: botResponse, sender: 'bot' }]);

        } catch (err) {
            console.error('Error sending message to chatbot:', err);
            setChatError(`Failed to connect to chatbot: ${err.message}`);
            setMessages(prevMessages => [...prevMessages, { text: "Error: Could not get a response from the chatbot.", sender: 'bot', isError: true }]);
        } finally {
            setIsChatLoading(false);
        }
    };

    // If the widget is not visible, render nothing
    if (!isVisible) return null;

    return (
        <div className={isEmbedded ? "chatbot-widget-embedded" : "chatbot-widget-fixed"}>
            <div className={!isEmbedded ? "chatbot-widget-container" : undefined}>
                {/* Header */}
                {!isEmbedded && (
                    <div className="chatbot-widget-header">
                        <h2 className="chatbot-widget-header-title">AI summary</h2>
                        <div className="chatbot-widget-header-actions">
                            {(currentView === 'chatView' || currentView === 'error' || currentView === 'loadingSummary') && (
                                <button
                                    type="button"
                                    onClick={resetWidget}
                                    className="chatbot-widget-header-btn"
                                    aria-label="Back to Contact Form"
                                    title="Back to Contact Form"
                                >
                                    <svg className="chatbot-widget-header-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                                    </svg>
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={onClose}
                                className="chatbot-widget-header-btn"
                                aria-label="Close Chat"
                                title="Close Chat"
                            >
                                <svg className="chatbot-widget-header-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                                </svg>
                            </button>
                        </div>
                    </div>
                )}

                {/* Content Area */}
                {currentView === 'contactForm' && (
                    <div className="chatbot-widget-content chatbot-widget-content-form">
                        {initialEmail || initialPhoneNumber ? (
                            <div className="chatbot-widget-auto-start">
                                <p>Starting with: <span className="ds-strong">{initialEmail || initialPhoneNumber}</span></p>
                                <Loader label="Loading student information..." />
                            </div>
                        ) : (
                        <form onSubmit={handleSubmitContact} className="chatbot-widget-form">
                            <Field label="Email ID (Optional)">
                                {(ctl) => (
                                    <TextInput
                                        {...ctl}
                                        type="email"
                                        value={email}
                                        onChange={(e) => { setEmail(e.target.value); setPhoneNumber(''); }}
                                        placeholder="your.email@example.com"
                                        disabled={isSummarizing}
                                    />
                                )}
                            </Field>
                            <div className="chatbot-widget-or ds-text-subtle">OR</div>
                            <Field label="Phone Number (Optional)">
                                {(ctl) => (
                                    <TextInput
                                        {...ctl}
                                        type="tel"
                                        value={phoneNumber}
                                        onChange={(e) => { setPhoneNumber(e.target.value); setEmail(''); }}
                                        placeholder="e.g., +919876543210"
                                        disabled={isSummarizing}
                                    />
                                )}
                            </Field>
                            {summarizationError && currentView === 'contactForm' && (
                                <Callout tone="danger">{summarizationError}</Callout>
                            )}
                            <Button
                                type="submit"
                                variant="primary"
                                block
                                loading={isSummarizing}
                                disabled={isSummarizing || (!email && !phoneNumber)}
                            >
                                {isSummarizing ? null : 'Get Summary'}
                            </Button>
                        </form>
                        )}
                    </div>
                )}

                {currentView === 'loadingSummary' && (
                    <div className="chatbot-widget-content chatbot-widget-content-loading">
                        <Loader label="Processing summary..." />
                        <p className="chatbot-widget-loading-desc ds-text-muted">Please wait, this may take a moment as the agent fetches and summarizes tickets.</p>
                        {summarizationError && (
                            <Callout tone="danger">{summarizationError}</Callout>
                        )}
                    </div>
                )}

                {currentView === 'chatView' && (
                    <div className="chatbot-widget-content chatbot-widget-content-chat">
                        {/* Info Tabs */}
                        <div className="chatbot-widget-tabs">
                            <Tabs
                                ariaLabel="Fresh Desk summary sections"
                                value={activeInfoTab}
                                onChange={setActiveInfoTab}
                                items={[
                                    { id: 'summary', label: 'Summary' },
                                    { id: 'customerInfo', label: 'Profile' },
                                    { id: 'agentTips', label: 'Tips' },
                                    { id: 'chat', label: 'Chat' },
                                ]}
                            />
                        </div>

                        {/* Tab Content Area */}
                        <div className="chatbot-widget-tab-content">
                            {activeInfoTab === 'summary' && (
                                <div className="ds-stack chatbot-widget-sections">
                                    <Callout
                                        tone="neutral"
                                        title="Ticket Summary"
                                        action={ticketURL ? (
                                            <a
                                                href={ticketURL}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="ds-btn ds-btn--secondary ds-btn--sm"
                                            >
                                                View Tickets
                                            </a>
                                        ) : undefined}
                                    >
                                        {Array.isArray(summary) && summary.length > 0 ? (
                                            <ul className="chatbot-widget-list">
                                                {summary.map((item, idx) => (
                                                    <li key={idx}>{item}</li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p className="chatbot-widget-muted ds-text-subtle">No detailed summary available yet.</p>
                                        )}
                                    </Callout>

                                    {messageSummary && (
                                        <Callout
                                            tone="neutral"
                                            title="Conversation Summary"
                                            action={convoURL ? (
                                                <a
                                                    href={convoURL}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="ds-btn ds-btn--secondary ds-btn--sm"
                                                >
                                                    View Conversation
                                                </a>
                                            ) : undefined}
                                        >
                                            <p className="chatbot-widget-muted ds-text-muted">{messageSummary}</p>
                                            <h4 className="chatbot-widget-subheading">Sentiment</h4>
                                            {Latestsentiment && (
                                                <p className="chatbot-widget-muted ds-text-muted">{Latestsentiment} {getEmoji(emoji2)}</p>
                                            )}
                                        </Callout>
                                    )}
                                </div>
                            )}

                            {activeInfoTab === 'customerInfo' && (
                                <div className="ds-stack chatbot-widget-sections">
                                    <Callout tone="info" title="Customer Profile">
                                        {Object.keys(customerInfo).length > 0 ? (
                                            <DescriptionList
                                                items={Object.entries(customerInfo).map(([key, value]) => ({
                                                    label: <span className="chatbot-widget-capitalize">{key.replace(/([A-Z])/g, ' $1')}</span>,
                                                    value,
                                                }))}
                                            />
                                        ) : (
                                            <p className="chatbot-widget-muted ds-text-subtle">No detailed customer information available.</p>
                                        )}
                                    </Callout>
                                    {sentiment && (
                                        <Callout tone="neutral" title="Sentiments">
                                            <p className="chatbot-widget-muted">
                                                {sentiment} {getEmoji(emoji)} {/* Display emoji next to sentiment */}
                                            </p>
                                        </Callout>
                                    )}
                                </div>
                            )}

                            {activeInfoTab === 'agentTips' && (
                                <div className="ds-stack chatbot-widget-sections">
                                    <Callout tone="warning" title="Agent Tips">
                                        {Array.isArray(agentTips) && agentTips.length > 0 ? (
                                            <ul className="chatbot-widget-list">
                                                {agentTips.map((tip, idx) => (
                                                    <li key={idx}>{tip}</li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p className="chatbot-widget-muted ds-text-subtle">No specific tips available for this customer.</p>
                                        )}
                                    </Callout>
                                    <Callout tone="success" title="Actionable Insights">
                                        {Array.isArray(actionableInsights) && actionableInsights.length > 0 ? (
                                            <ul className="chatbot-widget-list">
                                                {actionableInsights.map((insight, idx) => (
                                                    <li key={idx}>{insight}</li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p className="chatbot-widget-muted ds-text-subtle">No specific actions required, user has no active tickets!</p>
                                        )}
                                    </Callout>
                                </div>
                            )}

                            {activeInfoTab === 'chat' && (
                                <div className="chatbot-widget-chat">
                                    <div className="chatbot-widget-messages">
                                        {messages.length === 0 && !isChatLoading && (
                                            <p className="chatbot-widget-chat-empty ds-text-subtle">
                                                Type a message below to start chatting with the bot.
                                            </p>
                                        )}
                                        {messages.map((msg, index) => (
                                            <div
                                                key={index}
                                                className={`chatbot-widget-message chatbot-widget-message--${msg.sender === 'user' ? 'user' : 'bot'}`}
                                            >
                                                <div
                                                    className={`chatbot-widget-bubble chatbot-widget-bubble--${msg.sender === 'user' ? 'user' : 'bot'}${msg.isError ? ' chatbot-widget-bubble--error' : ''}`}
                                                >
                                                    {msg.text}
                                                </div>
                                            </div>
                                        ))}
                                        {isChatLoading && (
                                            <div className="chatbot-widget-message chatbot-widget-message--bot">
                                                <div className="chatbot-widget-bubble chatbot-widget-bubble--bot">
                                                    <Loader label="Typing..." />
                                                </div>
                                            </div>
                                        )}
                                        <div ref={messagesEndRef} /> {/* Scroll target */}
                                    </div>

                                    {chatError && (
                                        <Callout tone="danger">{chatError}</Callout>
                                    )}

                                    {/* Chat Input */}
                                    <form onSubmit={handleSendMessage} className="chatbot-widget-compose">
                                        <TextInput
                                            type="text"
                                            aria-label="Message"
                                            value={newMessage}
                                            onChange={(e) => setNewMessage(e.target.value)}
                                            className="chatbot-widget-compose-input"
                                            placeholder="Type your message..."
                                            disabled={isChatLoading}
                                        />
                                        <Button
                                            type="submit"
                                            variant="primary"
                                            icon={<SendIcon />}
                                            disabled={isChatLoading || !newMessage.trim()}
                                            aria-label="Send Message"
                                        />
                                    </form>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {currentView === 'error' && (
                    <div className="chatbot-widget-content chatbot-widget-content-error">
                        <Callout tone="danger" title="An Error Occurred!">
                            {summarizationError || chatError || "Something went wrong. Please try again."}
                        </Callout>
                        <Button
                            variant="primary"
                            onClick={() => {
                                setSummarizationError('');
                                setChatError('');
                                setIsSummarizing(false);
                                setCurrentView('contactForm');
                                setTimeout(() => {
                                    if (initialPhoneNumber) {
                                        setPhoneNumber(initialPhoneNumber);
                                        handleSubmitContact(null, '', initialPhoneNumber);
                                    } else if (initialEmail) {
                                        setEmail(initialEmail);
                                        handleSubmitContact(null, initialEmail, '');
                                    }
                                }, 0);
                            }}
                        >
                            Try Again
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
